import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  LocalOffer as LocalOfferIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  fetchCities,
  fetchAreasByCity,
  fetchAllTags,
  getAllCategoriesForUser,
  updateVendorProfileUnified,
  fetchVendorWithAllData,
  getCurrentUser
} from '../services/api';
import { API_CONFIG } from '../config/api.config';
import { saveDeductedLocation, getDeductedLocation } from '../utils/locationStorage';

const VendorEditProfile = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Redirect if not vendor
  useEffect(() => {
    if (!currentUser || currentUser.user_type !== 'vendor') {
      navigate('/auth');
    }
  }, [currentUser, navigate]);

  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    whatsapp: currentUser?.whatsapp || '',
    business_name: currentUser?.business_name || '',
    address: currentUser?.address || '',
    city_id: currentUser?.city_id || '',
    area_id: currentUser?.area_id || '',
    description: currentUser?.description || '',
    working_hours: currentUser?.working_hours || '',
    home_visit_charges: currentUser?.home_visit_charges || '',
    selectedTags: currentUser?.tags || [],
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Parse existing hours and set initial working days
  const parseHours = (hoursString) => {
    try {
      // Format: "Mon, Tue, Wed, Thu, Fri (09:00-18:00)"
      const match = hoursString.match(/(.+?)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
      if (match) {
        const daysStr = match[1];
        const start = match[2];
        const end = match[3];
        const days = daysStr.split(',').map(d => d.trim());
        return { days, start, end };
      }
    } catch (err) {
      console.error('Error parsing hours:', err);
    }
    return { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], start: '09:00', end: '18:00' };
  };

  const initialHours = parseHours(currentUser?.working_hours || '');
  const [workingDays, setWorkingDays] = useState(initialHours.days);
  const [timeRange, setTimeRange] = useState({ start: initialHours.start, end: initialHours.end });

  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deductedLocation, setDeductedLocation] = useState(null);
  const [locationExpiringSoon, setLocationExpiringSoon] = useState(false);

  // Initial Data Load
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Check for previously saved location
        const saved = getDeductedLocation();
        if (saved) {
          setDeductedLocation(saved);
          setFormData(prev => ({
            ...prev,
            city_id: saved.city_id?.toString() || prev.city_id,
            area_id: saved.area_id?.toString() || prev.area_id
          }));
        }

        const [citiesData, tagsData, categoriesWithSelection] = await Promise.all([
          fetchCities(),
          fetchAllTags(),
          getAllCategoriesForUser(currentUser.id),
        ]);
        setCities(citiesData);
        setAllTags(tagsData || []);
        setCategoriesData(categoriesWithSelection);

        const selected = [];
        categoriesWithSelection.forEach(parent => {
          if (parent.is_selected) selected.push(parent.id);
          parent.children?.forEach(child => {
            if (child.is_selected) selected.push(child.id);
          });
        });
        setSelectedCategoryIds(selected);

        if (formData.city_id) {
          const areasData = await fetchAreasByCity(formData.city_id);
          setAreas(areasData);
        }

        // Auto-fill from geolocation if no saved location
        if (!saved && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              reverseGeocodeAndAutoFillLocation(pos.coords.latitude, pos.coords.longitude, citiesData);
            },
            () => console.log('Geolocation not available')
          );
        }
      } catch (err) {
        setError('Failed to load form data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const reverseGeocodeAndAutoFillLocation = async (lat, lng, citiesData) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/detect-location?lat=${lat}&lng=${lng}`);
      const data = await response.json();

      if (data.city_id) {
        // Save deducted location with timestamp
        saveDeductedLocation({
          city_id: data.city_id,
          city_name: data.city_name,
          area_id: data.area_id,
          area_name: data.area_name,
          lat,
          lng
        });
        setDeductedLocation(data);
        setFormData(prev => ({
          ...prev,
          city_id: data.city_id?.toString() || prev.city_id,
          area_id: data.area_id?.toString() || prev.area_id
        }));

        // Load areas for the new city
        if (data.city_id) {
          const areasData = await fetchAreasByCity(data.city_id);
          setAreas(areasData);
        }
      }
    } catch (err) {
      console.error('Location detection failed:', err);
    }
  };

  // Monitor location expiry
  useEffect(() => {
    if (!deductedLocation) return;

    const interval = setInterval(() => {
      const saved = getDeductedLocation();
      if (!saved) {
        setDeductedLocation(null);
        setLocationExpiringSoon(false);
        clearInterval(interval);
      } else {
        // Show warning when less than 1 minute remaining
        const stored = localStorage.getItem('deducted_location');
        if (stored) {
          const data = JSON.parse(stored);
          const elapsed = Date.now() - data.timestamp;
          const remaining = 5 * 60 * 1000 - elapsed;
          setLocationExpiringSoon(remaining < 60 * 1000);
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [deductedLocation]);

  // Category Filter Logic
  useEffect(() => {
    if (categorySearch.trim().length > 0) {
      const search = categorySearch.toLowerCase();
      const filtered = [];
      categoriesData.forEach(parent => {
        if (parent.name.toLowerCase().includes(search)) filtered.push(parent);
        parent.children?.forEach(child => {
          if (child.name.toLowerCase().includes(search)) filtered.push(child);
        });
      });
      setFilteredCategories(filtered.slice(0, 5));
    } else {
      setFilteredCategories([]);
    }
  }, [categorySearch, categoriesData]);

  // Tag Filter Logic
  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(search) &&
        !formData.selectedTags.some(selected => selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 5));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, formData.selectedTags]);

  const handleCityChange = async (e) => {
    // Don't allow city changes if location is deducted
    if (deductedLocation) return;
    
    const cityId = e.target.value;
    setFormData(prev => ({ ...prev, city_id: cityId, area_id: '' }));
    if (cityId) {
      const areasData = await fetchAreasByCity(cityId);
      setAreas(areasData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (categoryId) => {
    setSelectedCategoryIds(prev => {
      const newIds = [...prev];
      const index = newIds.indexOf(categoryId);
      if (index > -1) newIds.splice(index, 1);
      else newIds.push(categoryId);
      return newIds;
    });
  };

  const handleCategorySelectFromSearch = (categoryId) => {
    let selectedCategory = categoriesData.find(cat => cat.id === categoryId);
    let parentId = categoryId;

    if (!selectedCategory) {
      for (const parent of categoriesData) {
        const child = parent.children?.find(c => c.id === categoryId);
        if (child) {
          selectedCategory = child;
          parentId = parent.id;
          break;
        }
      }
    }

    if (!selectedCategory) return;

    setSelectedCategoryIds(prev => {
      const newIds = [...prev];
      if (selectedCategory.children) {
        if (!newIds.includes(selectedCategory.id)) newIds.push(selectedCategory.id);
        selectedCategory.children?.forEach(c => { if (!newIds.includes(c.id)) newIds.push(c.id); });
      } else {
        if (!newIds.includes(selectedCategory.id)) newIds.push(selectedCategory.id);
        if (!newIds.includes(parentId)) newIds.push(parentId);
      }
      return newIds;
    });
    setCategorySearch('');
  };

  const handleTagSelect = (tag) => {
    setFormData(prev => ({ ...prev, selectedTags: [...prev.selectedTags, tag] }));
    setTagSearch('');
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.filter(tag => (tag.id ? tag.id !== tagToRemove.id : tag.name !== tagToRemove.name))
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !formData.selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) handleTagSelect(existingTag);
        else {
          setFormData(prev => ({ ...prev, selectedTags: [...prev.selectedTags, { id: null, name: tagName }] }));
          setTagSearch('');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setSubmitting(true);
      if (!formData.name.trim() || !formData.phone.trim()) throw new Error('Name and phone are required');
      if (selectedCategoryIds.length === 0) throw new Error('Please select at least one service');

      // Format working hours
      const hoursFormatted = workingDays.length > 0
        ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
        : 'Closed / Appointment Only';

      const submitData = {
        ...formData,
        working_hours: hoursFormatted,
        category_ids: selectedCategoryIds,
        tags: formData.selectedTags.map(tag => ({ id: tag.id || null, name: tag.name }))
      };

      await updateVendorProfileUnified(currentUser.id, submitData);
      await fetchVendorWithAllData(currentUser.id);

      setSuccess('Profile updated successfully!');
      setTimeout(() => navigate('/vendor-dashboard'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#4f46e5' }} />
      </Box>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.card}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.iconCircle}><PersonIcon sx={{ color: '#4f46e5' }} /></div>
          <h1 style={styles.title}>Edit Profile</h1>
          <p style={styles.subtitle}>Keep your business info updated to attract more customers</p>
        </header>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {success && <div style={styles.successBanner}>{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Section: Personal */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <PersonIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Personal Details</h3>
            </div>
            
            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Full Name *</label>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="e.g. John Doe"
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Email Address</label>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="john@example.com"
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Phone Number</label>
                <TextField
                  fullWidth
                  value={formData.phone}
                  disabled
                  variant="outlined"
                  size="small"
                  sx={styles.disabledInput}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><Tooltip title="Cannot be changed"><LocalOfferIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></Tooltip></InputAdornment>
                  }}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>WhatsApp Number</label>
                <TextField
                  fullWidth
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="+91..."
                  sx={styles.muiInput}
                />
              </div>
            </div>
          </section>

          {/* Section: Business */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <BusinessIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Business Info</h3>
            </div>

            <div style={styles.inputWrapper}>
              <label style={styles.label}>Business / Shop Name</label>
              <TextField
                fullWidth
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                placeholder="e.g. JD Electricals"
                sx={styles.muiInput}
              />
            </div>

            {deductedLocation && (
              <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                📍 <strong>Location detected from device:</strong> {deductedLocation.city_name}{deductedLocation.area_name ? `, ${deductedLocation.area_name}` : ''}
                {locationExpiringSoon && <span style={{ color: '#dc2626', marginLeft: '8px' }}>⚠️ Expires soon</span>}
              </div>
            )}

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>City</label>
                <Select
                  fullWidth
                  value={formData.city_id}
                  onChange={handleCityChange}
                  size="small"
                  displayEmpty
                  disabled={!!deductedLocation}
                  sx={styles.muiInput}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map(city => <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>)}
                </Select>
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Area</label>
                <Select
                  fullWidth
                  name="area_id"
                  value={formData.area_id}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!formData.city_id || !!deductedLocation}
                  displayEmpty
                  sx={styles.muiInput}
                >
                  <MenuItem value="">Select Area</MenuItem>
                  {areas.map(area => <MenuItem key={area.id} value={area.id}>{area.name}</MenuItem>)}
                </Select>
              </div>
            </div>

            <div style={styles.inputWrapper}>
              <label style={styles.label}>Full Address</label>
              <TextField
                fullWidth
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
                placeholder="Shop number, street, landmark..."
                sx={styles.muiInput}
              />
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Working Days</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {daysList.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${workingDays.includes(day) ? '#4f46e5' : '#e2e8f0'}`,
                        background: workingDays.includes(day) ? '#4f46e5' : 'white',
                        color: workingDays.includes(day) ? 'white' : '#1e293b',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: '0.2s'
                      }}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Opens At</label>
                <input
                  type="time"
                  value={timeRange.start}
                  onChange={e => setTimeRange({ ...timeRange, start: e.target.value })}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Closes At</label>
                <input
                  type="time"
                  value={timeRange.end}
                  onChange={e => setTimeRange({ ...timeRange, end: e.target.value })}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Visit Charges</label>
                <TextField
                  fullWidth
                  name="home_visit_charges"
                  value={formData.home_visit_charges}
                  onChange={handleInputChange}
                  placeholder="₹200 or Free"
                  size="small"
                  sx={styles.muiInput}
                />
              </div>
            </div>
          </section>

          {/* Section: Description */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <CheckCircleIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>About your services</h3>
            </div>
            <TextField
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Describe what makes your service the best in the neighborhood..."
              sx={styles.muiInput}
            />
          </section>

          {/* Section: Categories */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <SearchIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Services Provided *</h3>
            </div>
            
            <Box sx={{ position: 'relative', mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search services (e.g. Electrician, Plumber)"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} /> }}
                sx={styles.muiInput}
              />
              {categorySearch && filteredCategories.length > 0 && (
                <div style={styles.suggestionBox}>
                  {filteredCategories.map(cat => (
                    <div key={cat.id} onClick={() => handleCategorySelectFromSearch(cat.id)} style={styles.suggestionItem}>
                      {cat.icon} {cat.name}
                    </div>
                  ))}
                </div>
              )}
            </Box>

            <div style={styles.chipContainer}>
              {categoriesData.map(parent => {
                const isParentSelected = selectedCategoryIds.includes(parent.id);
                return (
                  <React.Fragment key={parent.id}>
                    <Chip
                      label={`${parent.icon} ${parent.name}`}
                      onClick={() => handleServiceToggle(parent.id)}
                      sx={{
                        ...styles.parentChip,
                        backgroundColor: isParentSelected ? '#4f46e5' : '#fff',
                        color: isParentSelected ? '#fff' : '#1e293b',
                        borderColor: isParentSelected ? '#4f46e5' : '#e2e8f0',
                      }}
                    />
                    {parent.children?.map(child => {
                      const isChildSelected = selectedCategoryIds.includes(child.id);
                      if (!isParentSelected && !isChildSelected) return null;
                      return (
                        <Chip
                          key={child.id}
                          label={child.name}
                          onClick={() => handleServiceToggle(child.id)}
                          size="small"
                          sx={{
                            ...styles.childChip,
                            backgroundColor: isChildSelected ? '#10b981' : '#f0fdf4',
                            color: isChildSelected ? '#fff' : '#047857',
                          }}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </section>

          {/* Section: Tags */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <LocalOfferIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Search Tags</h3>
            </div>
            <p style={styles.infoText}>Add keywords like "emergency", "repair", "cheap" to appear in searches.</p>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type tag and press Enter"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                sx={styles.muiInput}
              />
              {tagSearch && filteredTags.length > 0 && (
                <div style={styles.suggestionBox}>
                  {filteredTags.map(tag => (
                    <div key={tag.id} onClick={() => handleTagSelect(tag)} style={styles.suggestionItem}>{tag.name}</div>
                  ))}
                </div>
              )}
            </Box>
            <div style={styles.chipContainer}>
              {formData.selectedTags.map(tag => (
                <Chip
                  key={tag.id || tag.name}
                  label={tag.name}
                  onDelete={() => handleTagRemove(tag)}
                  sx={styles.tagChip}
                />
              ))}
            </div>
          </section>

          {/* Buttons */}
          <div style={styles.actions}>
            <button type="button" onClick={() => navigate('/vendor-dashboard')} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" disabled={submitting} style={styles.submitBtn}>
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '24px 16px',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '32px',
    border: '1px solid #e2e8f0',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  iconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  sectionIcon: {
    color: '#4f46e5',
    fontSize: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#334155',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569',
  },
  muiInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fff',
      transition: '0.2s',
      '&:hover': { backgroundColor: '#fdfdfd' },
      '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 4px rgba(79, 70, 229, 0.1)' }
    }
  },
  disabledInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#f8fafc',
    }
  },
  suggestionBox: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    border: '1px solid #e2e8f0',
    marginTop: '4px',
    overflow: 'hidden',
  },
  suggestionItem: {
    padding: '12px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#f1f5f9' },
    borderBottom: '1px solid #f1f5f9',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  parentChip: {
    fontWeight: 600,
    fontSize: '13px',
    border: '1px solid #e2e8f0',
  },
  childChip: {
    fontSize: '12px',
    fontWeight: 500,
  },
  tagChip: {
    backgroundColor: '#eef2ff',
    color: '#4f46e5',
    fontWeight: 600,
    borderRadius: '8px',
  },
  infoText: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '12px',
  },
  actions: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '12px 24px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    fontWeight: 600,
    cursor: 'pointer',
    transition: '0.2s',
  },
  submitBtn: {
    padding: '12px 32px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#4f46e5',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
    transition: '0.2s',
    '&:hover': { backgroundColor: '#4338ca' }
  },
  errorBanner: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '24px',
    border: '1px solid #fee2e2',
  },
  successBanner: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#15803d',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '24px',
    border: '1px solid #dcfce7',
  }
};

export default VendorEditProfile;